import {
	type AssociationMembershipResponse,
	AssociationRole,
	Status,
} from '~~/openapi-client/gen/schemas'

/**
 * Ein Composable, das Informationen über die Mitgliedschaft des aktuell angemeldeten Benutzers in den ausgewählten
 * Verein bereitstellt.
 */
export default function useUserMembership() {
	const { $api } = useNuxtApp()
	const { selectedAssociation } = useAssociations()
	const { verificationStatus } = useIdnow()

	const { refresh: refreshMembership, data: membership } = useAsyncData(
		'membership',
		() => {
			console.debug('Loading membership for association ID:', selectedAssociation.value?.id)
			if (selectedAssociation.value) {
				return $api.associationMembershipApi.findByAccountAndAssociationId(selectedAssociation.value.id)
			} else {
				return Promise.resolve(undefined)
			}
		},
		{
			immediate: false,
			watch: [selectedAssociation],
			transform: user => (user?.data) as AssociationMembershipResponse,
		},
	)

	const { refresh: refreshMemberships, data: memberships } = useAsyncData(
		'memberships',
		() => $api.associationMembershipApi.findAllByAccount(),
		{
			immediate: false,
			default: () => [] as AssociationMembershipResponse[],
			transform: memberships => (memberships?.data) as AssociationMembershipResponse[],
		},
	)

	/**
	 * Lädt die Daten des aktuell angemeldeten Benutzers neu.
	 */
	const refresh = async () => {
		await refreshMembership()
		await refreshMemberships()
	}

	/**
	 * Gibt zurück, ob der angemeldete Benutzer video-legitimiert ist.
	 */
	const amIVideoLegitimized = computed(() => verificationStatus.value === Status.SUCCESS)

	/**
	 * Gibt zurück, ob der angemeldete Benutzer ein Systembenutzer ist.
	 * Systembenutzer sind Mitgliederverwalter und Vorstandsmitglieder.
	 */
	const amISystemUser = computed(() => membership.value?.role !== AssociationRole.MEMBER)

	/**
	 * Gibt zurück, ob der angemeldete Benutzer ein Vorstandsmitglied ist.
	 * In anderen Worten, ob die Rolle des Benutzers 'ADMIN' ist.
	 */
	const amIBoardMember = computed(() => membership.value?.role === AssociationRole.ADMIN)

	/**
	 * Gibt zurück, ob das angegebene Mitglied bearbeitet werden kann.
	 * @param member
	 */
	const canIEditMember = (member: AssociationMembershipResponse) => {
		if (amIBoardMember.value) {
			return member.id === membership.value?.id || member.role !== AssociationRole.ADMIN
		} else {
			return member.role !== AssociationRole.ADMIN
		}
	}

	const salutation = computed(() => {
		switch (membership.value?.gender) {
			case 'MALE':
				return 'Herr'
			case 'FEMALE':
				return 'Frau'
			default:
				return '*'
		}
	})

	/**
	 * Gibt zurück, ob die E-Mail-Adresse des angegebenen Mitglieds bearbeitet werden kann.
	 * Systemnutzer (MANAGE_USERS, ADMIN) können die E-Mail-Adresse bearbeiten eines Mitglieds (MEMBERS) bearbeiten
	 * @param member
	 */
	const canIEditEmail = (member?: AssociationMembershipResponse) => {
		return member?.role === AssociationRole.MEMBER ? true : !member?.contactInformationResponse?.email
	}

	/**
	 * Gibt zurück, ob der angemeldete Benutzer seine Daten vollständig erfasst wurde.
	 */
	const isMyMembershipDataComplete = computed(() => {
		switch (membership.value?.role) {
			case AssociationRole.ADMIN:
				// Vorstandsmitglieder müssen alle Felder ausgefüllt haben
				return !!(membership.value?.firstname.trim()
					&& membership.value?.lastname.trim()
					&& membership.value?.gender?.trim()
					&& membership.value?.birthday?.trim()
					&& membership.value?.iban?.trim()
					&& membership.value?.joinDate?.trim()
					&& membership.value?.contactInformationResponse?.email?.trim()
					&& membership.value?.contactInformationResponse?.phone?.trim()
					&& membership.value?.contactInformationResponse?.address.city?.trim()
					&& membership.value?.contactInformationResponse?.address.street?.trim()
					&& membership.value?.contactInformationResponse?.address.postalCode?.trim()
					&& membership.value?.contactInformationResponse?.address.houseNumber?.trim())
			case AssociationRole.MANAGE_USERS:
				return !!(membership.value?.firstname.trim()
					&& membership.value?.lastname.trim()
					&& membership.value?.contactInformationResponse?.email?.trim())
			default:
				return false
		}
	})

	const amIOnboarded = computed<boolean>(() => {
		if (membership.value) {
			switch (membership.value?.role) {
				default:
				case AssociationRole.ADMIN:
					return !!membership.value
						&& amIVideoLegitimized.value
						&& isMyMembershipDataComplete.value
				case AssociationRole.MANAGE_USERS:
					return true
			}
		} else {
			return false
		}
	})

	/**
	 * Gibt zurück, ob der angemeldete Benutzer mindestens eine Mitgliedschaft hat.
	 */
	const doIHaveAnyMemberships = computed(() => memberships.value?.length > 0)

	return {
		membership,
		memberships,
		salutation,
		amISystemUser,
		amIBoardMember,
		isMyMembershipDataComplete,
		amIVideoLegitimized,
		amIOnboarded,
		doIHaveAnyMemberships,
		canIEditMember,
		canIEditEmail,
		refresh,
	}
}
